import React, { useState } from 'react';
import { Stepper, StepProps, useSteps } from '@orbita-ui/core';
import { useNavigate, useParams } from 'react-router-dom';
import Page from '../../components/page';
import FirstStep, { FirstStepFormInputs } from '../../features/card-products/new/first-step';
import SecondStep, { SecondStepFormInputs } from '../../features/card-products/new/second-step';
import ThirdStep, { ThirdStepFormInputs } from '../../features/card-products/new/third-step';
import FourthStep, { FourthStepFormInputs } from '../../features/card-products/new/fourth-step';
import { CreateCardProduct, ICardConfigurations, ICashback, IConfigurationDTO } from '../../interfaces/card-products';
import { getDateFormattedTwo } from '../../utils/date';
import useCreateCardProduct from '../../hooks/useCreateCardProduct';
import useToastContext from '../../hooks/useToastContext';


const NewCardProduct = () => {
  const [firstStepData, setFirstStepData] = useState<FirstStepFormInputs | null>(null);
  const [secondStepData, setSecondStepData] = useState<SecondStepFormInputs | null>(null);
  const [thirdStepData, setThirdStepData] = useState<ThirdStepFormInputs | null>(null);
  const [fourthStepData, setFourthStepData] = useState<FourthStepFormInputs | null>(null);
  const toast = useToastContext();
  const navigate = useNavigate();
  const { issuer } = useParams<{ issuer: string }>();

  const {cpLoading: isLoading, cpError: isError, createCardProduct } = useCreateCardProduct();

  const steps: StepProps[] = [
    {
      label: 'Completá los datos principales',
      icon: 'UserIcon',
    },
    {
      label: 'Configurá el embozado',
      icon: 'CardsIcon',
    },
    {
      label: 'Agregale Limites',
      icon: 'AddIcon',
    },
    {
      label: 'Configuración final',
      icon: 'SettingsIcon',
    },
  ];

  const { activeStep, goToPrevious, goToNext } = useSteps({
    count: steps.length,
    index: 0,
  });

  const createCardConfig = (data: FourthStepFormInputs) => {
    const virtualConfig: IConfigurationDTO = { allowed: data?.virtual }
    const physicalConfig: IConfigurationDTO = { allowed: data?.physical }
    const expConfig: IConfigurationDTO = { allowed: data?.expDate }
    const cvvConfig: IConfigurationDTO = { allowed: data?.cvv }

    const cardConfig: ICardConfigurations = {
      virtual: virtualConfig,
      physical: physicalConfig,
      exp_date: expConfig,
      cvv: cvvConfig
    }

    return cardConfig;
  }
  const createCashBack = (data: ThirdStepFormInputs) => {
    const cashBack: ICashback = {
      min_amount: data?.minCashBackAmount,
      max_amount: data?.maxCashBackAmount
    }

    return cashBack;
  }
  const onCreateCardProduct = async (data: FourthStepFormInputs) => {
    setFourthStepData(data)

    const cardProduct: CreateCardProduct = {
      name: firstStepData?.name,
      bin: firstStepData?.bin,
      institution_id: firstStepData?.institutionId,
      range: firstStepData?.range,
      brand: firstStepData?.brand,
      active: firstStepData?.active,
      start_date: getDateFormattedTwo(firstStepData?.startDate?.toString()),
      end_date: getDateFormattedTwo(firstStepData?.endDate?.toString()),
      card_configurations: createCardConfig(data),
      cashback: createCashBack(thirdStepData),
      life_cycle: firstStepData?.lifeCycle,
      design: secondStepData?.design,
      logo: secondStepData?.logo,
      commercial_bin: secondStepData?.commercialBin,
      recording_code: secondStepData?.recordingCode,
      product_type: firstStepData?.productType,
      card_affinity_group_id: firstStepData?.card_affinity_group_id
    }

    try {
      const response = await createCardProduct({ ...cardProduct })

      if(response.status === 200 || response.status === 201) {
        toast.success('Exito', 'El Card Product se ha creado satisfactoriamente!.');
        setFirstStepData(null);
        setSecondStepData(null);
        setThirdStepData(null);
        setFourthStepData(null);
        setInterval(
          () => navigate(`/${issuer}/card-products`),
          3000
        );
      } else {
        throw new Error();
      }
    } catch (e: unknown) {
      toast.error('Error', `Ocurrió un error: ${isError}, porfavor, intenta nuevamente.`);
    }
  }

  return (
    <Page title="Nuevo Card Product" subtitle="Crea un nuevo card product">
      <Stepper activeStep={activeStep} steps={steps} />
      {activeStep === 0 && <FirstStep {...{ goToNext, setFirstStepData, firstStepData }} />}
      {activeStep === 1 && (
        <SecondStep {...{ goToNext, goToPrevious, setSecondStepData, firstStepData, secondStepData }} />
      )}
      {activeStep === 2 && <ThirdStep {...{ goToNext, goToPrevious, setThirdStepData, thirdStepData }} />}
      {activeStep === 3 && <FourthStep {...{goToPrevious, fourthStepData, onCreateCardProduct, isLoading }} />}
    </Page>
  );
}

export default NewCardProduct;
